import { useEffect, useState, lazy } from 'react'
import PropTypes from 'prop-types'

import { Box, LoadWithSuspense } from '@redriverpress/system'

import { recorderStatus } from 'Common/constants/mediaRecorder'
import useElapsingTime from 'Common/hooks/useElapsingTime'
import useMediaRecorder from 'Common/hooks/useMediaRecorder'
import RequestMicPermissionDialog from 'Common/components/dialogs/RequestMicPermissionDialog'
import ConfirmActionDialog from 'Common/components/dialogs/ConfirmActionDialog'

import Player from './Player'

const Recorder = lazy(() => import('./Recorder'))

const MAX_RECORDING_TIME = 2
export default function AudioRecorder({
  audio,
  onAudioRecorded,
  onDeleteAudio,
  shouldConfirmToDelete,
  onStatusChange,
  small,
}) {
  const {
    base64Media,
    startRecording,
    status,
    stopRecording,
    setStatus,
  } = useMediaRecorder({
    audio: true,
  })

  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(
    false
  )
  const [
    isRequestPermissionDialogOpen,
    setIsRequestPermissionDialogOpen,
  ] = useState(false)

  const [isDeleteAudioPending, setIsDeleteAudioPending] = useState(false)
  const [recordingTime, setRecordingTime] = useState('00:00')

  const handleDeleteResponse = () => {
    setIsConfirmationDialogOpen(false)
    setRecordingTime('00:00')
    setIsDeleteAudioPending(false)
    onDeleteAudio()
    setStatus(recorderStatus.IDLE)
  }

  const handleStartRecording = () => {
    if (status === recorderStatus.BLOCKED) {
      return setIsRequestPermissionDialogOpen(true)
    }
    startRecording()
  }

  const handleAudioDeletion = () => {
    if (shouldConfirmToDelete) {
      return setIsConfirmationDialogOpen(true)
    }

    handleDeleteResponse()
  }

  const handleClick = () => {
    if (status === recorderStatus.RECORDING) {
      stopRecording()
      return
    }

    handleStartRecording()
  }

  useElapsingTime({
    timeLimit: MAX_RECORDING_TIME,
    handleTimeOver: stopRecording,
    updateTimeCallback: setRecordingTime,
    shouldStartTimer: status === recorderStatus.RECORDING,
  })

  useEffect(() => {
    const saveAudioEffectsWrapper = () => {
      if (base64Media) {
        onAudioRecorded(base64Media)
        setStatus(recorderStatus.IDLE)
      }
    }

    saveAudioEffectsWrapper()
  }, [base64Media])

  useEffect(() => {
    onStatusChange(status)
  }, [status])

  return (
    <>
      <Box>
        {audio ? (
          <Player
            src={audio}
            onDelete={() => handleAudioDeletion(true)}
            showCurrentTime={false}
            showRemainingTime
            small={small}
          />
        ) : (
          <LoadWithSuspense
            component={Recorder}
            status={status}
            onClick={handleClick}
            recordingTime={recordingTime}
            small={small}
          />
        )}
      </Box>

      <ConfirmActionDialog
        isOpen={isConfirmationDialogOpen}
        onClose={() => setIsConfirmationDialogOpen(false)}
        isPending={isDeleteAudioPending}
        onConfirm={handleDeleteResponse}
        onOpenChange={isOpen => setIsConfirmationDialogOpen(isOpen)}
        subtitle='Are you sure you want to delete the recorded answer?'
        title='Delete answer'
      />

      <RequestMicPermissionDialog
        isOpen={isRequestPermissionDialogOpen}
        onConfirm={() => setIsRequestPermissionDialogOpen(false)}
        onOpenChange={isOpen => setIsRequestPermissionDialogOpen(isOpen)}
      />
    </>
  )
}

AudioRecorder.defaultProps = {
  small: false,
  shouldConfirmToDelete: false,
  audio: null,
}

AudioRecorder.propTypes = {
  audio: PropTypes.string,
  onAudioRecorded: PropTypes.func.isRequired,
  onDeleteAudio: PropTypes.func.isRequired,
  shouldConfirmToDelete: PropTypes.bool,
  onStatusChange: PropTypes.func.isRequired,
  small: PropTypes.bool,
}
